import { apiClient } from '@/lib/api-client';
import { downloadAuthenticatedFile } from '@/lib/download';
import type { BillingOverview, CheckoutSession, Invoice, Subscription } from '@/types/api';

/**
 * Cache key for the billing overview, shared by every caller.
 *
 * One key, because it is one resource. Three pages held it under two different keys, so
 * renewing a subscription refreshed the page you were on and left the access banner beside it
 * reading the pre-renewal answer.
 */
export const BILLING_OVERVIEW_KEY = ['billing-overview'] as const;

export const billingApi = {
  overview: () => apiClient.get<BillingOverview>('/billing'),

  startCheckout: (planId: string, gateway: string) =>
    apiClient.post<CheckoutSession>('/billing/checkout', { plan_id: planId, gateway }),

  /** Starts this account's free trial. Refused with a sentence when it cannot have one. */
  startTrial: () => apiClient.post<Subscription>('/billing/trial'),

  /**
   * A short-lived, authenticated link to the gateway's own billing portal.
   *
   * Fetched on click rather than with the page: the URL carries a token, so one sitting in a
   * cache or a React Query store is a way into somebody's billing details.
   */
  paymentPortal: () => apiClient.get<{ url: string; gateway: string }>('/billing/payment-portal'),

  cancelSubscription: (id: string, reason: string) =>
    apiClient.post<Subscription>(`/billing/subscriptions/${id}/cancel`, { reason }),

  resumeSubscription: (id: string) =>
    apiClient.post<Subscription>(`/billing/subscriptions/${id}/resume`),

  /**
   * Renewing is a *payment*, so this returns a checkout session to redirect to rather than a
   * subscription. The plan comes from the subscription on record — the client does not get to
   * choose which plan it renews onto.
   */
  renewSubscription: (id: string, gateway: string) =>
    apiClient.post<CheckoutSession>(`/billing/subscriptions/${id}/renew`, { gateway }),

  getInvoice: (id: string) => apiClient.get<Invoice>(`/billing/invoices/${id}`),

  /**
   * The invoice endpoint is authenticated, so the file cannot be fetched by a plain
   * link — a browser navigation would not carry the bearer token.
   */
  downloadInvoice: (id: string, number: string) =>
    downloadAuthenticatedFile(`/billing/invoices/${id}/download`, `${number}.pdf`),
};
